import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Header from "../../Common/Layout/Header";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useReactToPrint } from "react-to-print";
import BarcodeGenerator from "./BarcodeGenerator";
import { buildBoardingPassScanText, getPnrCodes } from "./boardingPassBarcode";
import { getProxiedImageUrl, swapProxiedImagesForCapture } from "../../Common/Helpers/imageProxy";

// Neutral, print-friendly palette — no brand colors, just ink/gray on white.
const ink = "#1a1a1a";
const muted = "#666666";
const faint = "#999999";
const line = "#d9d9d9";

const styles = {
  page: { background: "#eef0f2", minHeight: "100vh", padding: "20px 0" },
  actionBar: { display: "flex", justifyContent: "flex-end", gap: "10px", marginBottom: "20px", padding: "0 16px" },
  btn: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer", textDecoration: "none" },
  btnSolid: { background: ink, color: "#fff", border: `1px solid ${ink}` },
  btnOutline: { background: "#fff", color: ink, border: `1px solid ${ink}` },
  invoiceWrap: { maxWidth: "820px", margin: "0 auto", fontFamily: "'Segoe UI', Arial, sans-serif", color: ink },
  invoiceCard: { background: "#fff", border: `1px solid ${ink}`, marginBottom: "28px" },

  /* Header */
  invoiceHeader: { padding: "30px 40px 26px", borderBottom: `2px solid ${ink}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  invoiceTitleBlock: {},
  invoiceLabel: { fontSize: "11px", letterSpacing: "3px", textTransform: "uppercase", color: muted, marginBottom: "4px" },
  invoiceTitle: { fontSize: "28px", fontWeight: 800, color: ink, letterSpacing: "1px", lineHeight: 1 },
  invoiceTagline: { fontSize: "12px", color: muted, marginTop: "6px" },
  agencyBlock: { textAlign: "right" },
  agencyLogo: { maxHeight: "44px", maxWidth: "160px", objectFit: "contain", marginBottom: "8px" },
  agencyName: { fontSize: "18px", fontWeight: 800, color: ink },
  agencyInfo: { fontSize: "12px", color: muted, marginTop: "6px", lineHeight: 1.7 },

  /* Meta bar */
  metaBar: { background: "#f7f7f7", padding: "14px 40px", display: "flex", gap: "40px", flexWrap: "wrap", borderBottom: `1px solid ${line}` },
  metaItem: { color: muted, fontSize: "10px", letterSpacing: "1px", textTransform: "uppercase" },
  metaValue: { color: ink, fontWeight: 700, fontSize: "14px", marginTop: "3px" },

  /* Body */
  invoiceBody: { padding: "32px 40px" },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "28px" },
  infoBox: { background: "#f7f7f7", padding: "16px 20px", border: `1px solid ${line}` },
  infoBoxTitle: { fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: "8px" },
  infoLine: { fontSize: "13px", color: ink, marginBottom: "4px", fontWeight: 500 },
  infoLineBold: { fontSize: "14px", color: ink, fontWeight: 800 },

  /* Flight segment card */
  segCard: { border: `1px solid ${line}`, marginBottom: "14px", overflow: "hidden" },
  segCardHeader: { background: "#f7f7f7", padding: "12px 20px", display: "flex", alignItems: "center", gap: "12px", borderBottom: `1px solid ${line}` },
  airlineLogo: { width: "36px", height: "36px", objectFit: "contain", background: "#fff", padding: "2px", border: `1px solid ${line}` },
  airlineName: { fontWeight: 700, fontSize: "14px", color: ink },
  flightNum: { fontSize: "11px", color: muted },
  segCardBody: { padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" },
  iataSmall: { fontSize: "28px", fontWeight: 800, color: ink, letterSpacing: "-1px" },
  citySmall: { fontSize: "12px", color: muted, marginTop: "2px" },
  timeSmall: { fontSize: "20px", fontWeight: 700, color: ink },
  dateSmall: { fontSize: "11px", color: faint },
  airportSmall: { fontSize: "10px", color: faint, maxWidth: "120px" },
  arrowCenter: { textAlign: "center", flex: 1 },
  arrowLine: { borderTop: `1px dashed ${faint}`, margin: "8px 0" },
  durationBadge: { fontSize: "11px", color: faint, fontWeight: 600, letterSpacing: "1px", marginTop: "6px" },
  connectBadge: { border: `1px solid ${ink}`, padding: "4px 12px", fontSize: "11px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", margin: "8px 20px", display: "inline-block" },

  /* Divider */
  divider: { border: "none", borderTop: `1px solid ${line}`, margin: "24px 0" },
  sectionTitle: { fontSize: "13px", fontWeight: 700, color: ink, letterSpacing: "1px", textTransform: "uppercase", marginBottom: "12px" },

  /* Passengers table */
  table: { width: "100%", borderCollapse: "collapse", marginBottom: "24px" },
  th: { background: "#f7f7f7", color: ink, fontSize: "11px", letterSpacing: "1px", textTransform: "uppercase", padding: "10px 14px", textAlign: "left", fontWeight: 700, borderBottom: `2px solid ${ink}` },
  td: { padding: "10px 14px", fontSize: "13px", color: ink, borderBottom: `1px solid ${line}`, fontWeight: 500 },

  /* Price summary */
  priceBox: { background: "#f7f7f7", border: `1px solid ${line}`, overflow: "hidden", marginLeft: "auto", minWidth: "300px" },
  priceHeader: { background: ink, color: "#fff", padding: "10px 20px", fontSize: "12px", letterSpacing: "2px", textTransform: "uppercase", fontWeight: 700 },
  priceRow: { display: "flex", justifyContent: "space-between", padding: "10px 20px", borderBottom: `1px solid ${line}`, fontSize: "13px", color: ink },
  priceRowTotal: { display: "flex", justifyContent: "space-between", padding: "14px 20px", background: ink, fontSize: "16px", color: "#fff", fontWeight: 800 },
  priceLabel: { color: muted },
  priceValue: { fontWeight: 600, color: ink },
  discountValue: { fontWeight: 600, color: ink },

  /* Footer */
  invoiceFooter: { background: "#f7f7f7", borderTop: `2px solid ${ink}`, padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  footerNote: { fontSize: "11px", color: muted, maxWidth: "400px", lineHeight: 1.6 },
  footerBrand: { textAlign: "right" },
  footerBrandName: { fontWeight: 800, fontSize: "16px", color: ink },
  footerBrandSub: { fontSize: "11px", color: muted },

  /* Information Section */
  infoSection: { background: "#fff", borderTop: `1px solid ${line}`, padding: "24px 40px", fontFamily: "'Segoe UI', Arial, sans-serif" },
  infoTitleBar: { background: ink, color: "#fff", padding: "8px 16px", fontSize: "13px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "14px" },
  infoList: { paddingLeft: "0", listStyleType: "none", marginBottom: "0" },
  infoListItem: { fontSize: "11.5px", color: ink, marginBottom: "6px", lineHeight: "1.5", paddingLeft: "4px" },
  dangerSection: { display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "32px", marginTop: "24px", borderTop: `1px solid ${line}`, paddingTop: "20px" },
  dangerHeader: { fontSize: "11px", fontWeight: "bold", color: ink, marginBottom: "14px", lineHeight: "1.4", textTransform: "uppercase", letterSpacing: "0.5px" },
  dangerGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px 16px" },
  dangerItem: { fontSize: "11px", fontWeight: "500", color: ink, paddingLeft: "12px", position: "relative" },
};

const CustomerFlightInvoice = () => {
  const params = useParams();
  const contentRef = useRef();
  const [pageLoading, setPageLoading] = useState(false);
  const { user, loading } = useAuth();
  const [bookingDetails, setBookingDetails] = useState({});
  const [travellers, setTravellers] = useState([]);
  const [itinerary, setItinerary] = useState([]);

  const handlePrint = useReactToPrint({ content: () => contentRef.current });

  const handlePDF = () => {
    const input = contentRef.current;
    html2canvas(input, { useCORS: true, onclone: swapProxiedImagesForCapture }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(bookingDetails.travellerName + "_customer_invoice.pdf");
    });
  };

  function getTimeFromMins(mins) {
    var h = (mins / 60) | 0, m = mins % 60 | 0;
    return h + "h " + m + "m";
  }

  const GetData = () => {
    setPageLoading(true);
    apiService
      .getAll(config.GETFLIGHTBOOKINGDETAILS + "/" + params.flightBookingId)
      .then((res) => {
        setBookingDetails(res);
        setItinerary(res.dcFlightLegs);
        setTravellers(res.travellerInfos);
        setPageLoading(false);
      })
      .catch((err) => console.error("Error fetching company data", err));
  };

  useEffect(() => { GetData(); }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) return <p>Please log in</p>;
  const totalFare = parseFloat(bookingDetails.totalFare || 0);
  const totalPrice = parseFloat(bookingDetails.totalPrice || 0);

  // Extract unskewed basePrice and tax directly from the root properties (since API is updated)
  const baseFare = parseFloat(bookingDetails.baseFare || 0);
  const airlineTax = parseFloat(bookingDetails.tax || 0);

  const convinFee = parseFloat(bookingDetails.convenienceFee || 0);
  const pgAmount = parseFloat(bookingDetails.paymentGatewayAmount || 0);
  const markupVal = parseFloat(bookingDetails.markup || 0);
  const discountVal = parseFloat(bookingDetails.discount || 0);
  const platformFee = Math.max(0, totalPrice - totalFare - convinFee - markupVal - pgAmount + discountVal);
  const customerAirlineTax = airlineTax + platformFee;
  const displayedCustomerConvenience = convinFee + pgAmount + markupVal;

  const calculatedTotalCustomer = baseFare + customerAirlineTax + convinFee + pgAmount + markupVal - discountVal;

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="card-body" style={styles.page}>
                  {pageLoading ? (
                    <div className="row align-items-center justify-content-center">
                      <div className="col-lg-12">
                        <img src="/assets/images/loader.webp" width="64px" height="64px" alt="loading" loading="lazy" />
                        Loading, Please wait...
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={styles.actionBar} className="d-print-none">
                        <Link style={{ ...styles.btn, ...styles.btnOutline }} onClick={handlePrint}>
                          <i className="ri-printer-line"></i> Print
                        </Link>
                        <Link to={"/flight/voucher/" + params.flightBookingId} style={{ ...styles.btn, ...styles.btnOutline }}>
                          <i className="ri-file-text-line"></i> View Voucher
                        </Link>
                        <Link to={"/flight/invoice/" + params.flightBookingId} style={{ ...styles.btn, ...styles.btnOutline }}>
                          <i className="ri-file-text-line"></i> View Invoice
                        </Link>
                        <Link style={{ ...styles.btn, ...styles.btnSolid }} onClick={handlePDF}>
                          <i className="ri-download-2-line"></i> Download Customer Invoice
                        </Link>
                      </div>

                      <div ref={contentRef} style={styles.invoiceWrap}>
                        <div style={styles.invoiceCard}>
                          {/* Invoice Header */}
                          <div style={styles.invoiceHeader}>
                            <div style={styles.invoiceTitleBlock}>
                              <div style={styles.invoiceLabel}>Travel Agency</div>
                              <div style={styles.invoiceTitle}>CUSTOMER INVOICE</div>
                              <div style={styles.invoiceTagline}>Flight Booking Customer Invoice</div>
                            </div>
                            <div style={styles.agencyBlock}>
                              {user?.logo && (
                                <img src={user.logo} data-proxy-src={getProxiedImageUrl(user.logo)} alt={user?.companyname || "Logo"} style={styles.agencyLogo} loading="lazy" />
                              )}
                              <div style={styles.agencyName}>{user?.companyname}</div>
                              <div style={styles.agencyInfo}>
                                {user?.email}<br />
                                {user?.phoneNumber}<br />
                                GST: {user?.companygstno}
                              </div>
                            </div>
                          </div>

                          {/* Meta bar */}
                          <div style={styles.metaBar}>
                            {bookingDetails.bookingNo && (
                              <div style={styles.metaItem}>
                                Invoice / Booking No
                                <div style={styles.metaValue}>{bookingDetails.bookingNo}</div>
                              </div>
                            )}
                            <div style={styles.metaItem}>
                              Invoice Date
                              <div style={styles.metaValue}>{moment().format("DD MMM YYYY")}</div>
                            </div>
                            {bookingDetails.pnr && (
                              <div style={styles.metaItem}>
                                PNR
                                <div style={styles.metaValue}>{bookingDetails.pnr}</div>
                              </div>
                            )}
                            {bookingDetails.supplierBookingId && (
                              <div style={styles.metaItem}>
                                Supplier Ref
                                <div style={styles.metaValue}>{bookingDetails.supplierBookingId}</div>
                              </div>
                            )}
                            {bookingDetails.cabinClass && (
                              <div style={styles.metaItem}>
                                Class
                                <div style={styles.metaValue}>{bookingDetails.cabinClass}</div>
                              </div>
                            )}
                          </div>

                          {/* Body */}
                          <div style={styles.invoiceBody}>
                            {/* Bill To / Flight Summary */}
                            <div style={styles.twoCol}>
                              <div style={styles.infoBox}>
                                <div style={styles.infoBoxTitle}>Billed To</div>
                                <div style={styles.infoLineBold}>{bookingDetails.travellerName || travellers[0]?.firstName + " " + travellers[0]?.lastName}</div>
                                {bookingDetails.bookingNo && <div style={styles.infoLine}>Booking: {bookingDetails.bookingNo}</div>}
                                {bookingDetails.pnr && <div style={styles.infoLine}>PNR: {bookingDetails.pnr}</div>}
                              </div>
                              <div style={styles.infoBox}>
                                <div style={styles.infoBoxTitle}>Payment Summary</div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                  <span style={{ fontSize: "13px", color: muted }}>Total Amount</span>
                                  <span style={{ fontSize: "15px", fontWeight: 800, color: ink }}>
                                    INR {Math.round(calculatedTotalCustomer)?.toLocaleString("en-IN")} INR
                                  </span>
                                </div>
                                <div style={{ fontSize: "11px", color: faint }}>
                                  Includes all taxes &amp; fees
                                </div>
                                <div style={{ marginTop: "8px" }}>
                                  <span style={{ border: `1px solid ${ink}`, padding: "3px 10px", fontSize: "11px", fontWeight: 700, color: ink }}>
                                    {bookingDetails.bookingStatus}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Flight Segments */}
                            <div style={styles.sectionTitle}>Flight Details</div>
                            {itinerary.length > 0 && itinerary.map((it, itIdx) =>
                              it.dcDisplayLegs?.length > 0 && it.dcDisplayLegs.map((leg, legIdx) => (
                                <div key={`${itIdx}-${legIdx}`}>
                                  <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "10px" }}>
                                    <span style={{ fontSize: "18px", fontWeight: 800, color: ink }}>{leg.startSegment.airportCode}</span>
                                    <span style={{ color: faint, fontSize: "16px" }}>→</span>
                                    <span style={{ fontSize: "18px", fontWeight: 800, color: ink }}>{leg.endSegment.airportCode}</span>
                                    <span style={{ fontSize: "12px", color: muted }}>{leg.startSegment.city} to {leg.endSegment.city}</span>
                                    <span style={{ fontSize: "12px", color: faint, marginLeft: "auto" }}>
                                      {moment(leg.startSegment.departureDateTime).format("DD MMM YYYY")}
                                    </span>
                                    {it.flightLeg?.refundable && (
                                      <span style={{ border: `1px solid ${ink}`, padding: "3px 10px", fontSize: "11px", fontWeight: 700, color: ink }}>
                                        {it.flightLeg?.refundable}
                                      </span>
                                    )}
                                  </div>

                                  {it.dcSegments?.length > 0 && it.dcSegments.map((seg, segIdx) => (
                                    <div key={segIdx} style={styles.segCard}>
                                      <div style={styles.segCardHeader}>
                                        <img
                                          style={styles.airlineLogo}
                                          src={"/assets/images/airlinelogos/" + seg.tFAirlineData?.airlineCode + ".png"}
                                          alt={seg.tFAirlineData?.airline}
                                          onError={(e) => { e.target.style.display = "none"; }}
                                        loading="lazy" />
                                        <div>
                                          <div style={styles.airlineName}>{seg.tFAirlineData?.airline}</div>
                                          <div style={styles.flightNum}>{seg.tFAirlineData?.equimentType}-{seg.tFAirlineData?.flightNumber} &bull; {bookingDetails.cabinClass}</div>
                                        </div>
                                      </div>
                                      <div style={styles.segCardBody}>
                                        <div>
                                          <div style={styles.iataSmall}>{seg.tFDepartureData.airportCode}</div>
                                          <div style={styles.timeSmall}>{moment(seg.tFDepartureData.departureDateTime).format("HH:mm")}</div>
                                          <div style={styles.dateSmall}>{moment(seg.tFDepartureData.departureDateTime).format("DD MMM YYYY")}</div>
                                          <div style={styles.airportSmall}>{seg.tFDepartureData.airportName}{seg.tFDepartureData.terminal && `, T${seg.tFDepartureData.terminal}`}</div>
                                        </div>
                                        <div style={styles.arrowCenter}>
                                          <div style={styles.arrowLine}></div>
                                          <div style={styles.durationBadge}>{getTimeFromMins(seg.tFAirlineData?.duration)}</div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                          <div style={styles.iataSmall}>{seg.tFArrivalData.airportCode}</div>
                                          <div style={styles.timeSmall}>{moment(seg.tFArrivalData.arrivalDateTime).format("HH:mm")}</div>
                                          <div style={styles.dateSmall}>{moment(seg.tFArrivalData.arrivalDateTime).format("DD MMM YYYY")}</div>
                                          <div style={{ ...styles.airportSmall, textAlign: "right" }}>{seg.tFArrivalData.airportName}{seg.tFArrivalData.terminal && `, T${seg.tFArrivalData.terminal}`}</div>
                                        </div>
                                      </div>
                                      {seg.tFAirlineData.connectingTime !== 0 && (
                                        <span style={styles.connectBadge}>
                                          Layover: {getTimeFromMins(seg.tFAirlineData.connectingTime)}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ))
                            )}

                            <hr style={styles.divider} />

                            {/* Passengers */}
                            <div style={styles.sectionTitle}>Passenger Details</div>
                            <table style={styles.table}>
                              <thead>
                                <tr>
                                  <th style={styles.th}>#</th>
                                  <th style={styles.th}>Passenger Name</th>
                                  <th style={styles.th}>PNR</th>
                                  <th style={styles.th}>Baggage</th>
                                  <th style={styles.th}>Meals</th>
                                  <th style={styles.th}>Seat</th>
                                </tr>
                              </thead>
                              <tbody>
                                {travellers.length > 0 && travellers.map((t, idx) => (
                                  <tr key={idx} style={idx % 2 === 1 ? { background: "#f7f7f7" } : {}}>
                                    <td style={styles.td}>{idx + 1}</td>
                                    <td style={{ ...styles.td, fontWeight: 700 }}>
                                      <div>{t.title}. {t.firstName} {t.lastName}</div>
                                      <BarcodeGenerator
                                        value={buildBoardingPassScanText(bookingDetails, t)}
                                        size={80}
                                        style={{ marginTop: 4 }}
                                      />
                                    </td>
                                    <td style={styles.td}>{getPnrCodes(bookingDetails.pnr) || "—"}</td>
                                     <td>
  {t.dcBaggageTypes?.[0] ? (
    <div className="d-flex align-items-center gap-1">
      <span className="badge bg-light text-dark border" style={{ fontSize: '0.7rem' }}>
        {t.dcBaggageTypes[0].name}
      </span>
      <span>{t.dcBaggageTypes[0].description}</span>
    </div>
  ) : "—"}
</td>
                               <td>
  {t.dcMealTypes?.[0] ? (
    <div className="d-flex align-items-center gap-1">
      <span className="badge bg-light text-dark border" style={{ fontSize: '0.7rem' }}>
        {t.dcMealTypes[0].name}
      </span>
      <span>{t.dcMealTypes[0].description}</span>
    </div>
  ) : "—"}
</td>
                                    <td style={styles.td}>{t.dcSeatTypes?.[0]?.description || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            <hr style={styles.divider} />

                            {/* Price Breakdown */}
                            <div style={styles.sectionTitle}>Fare Breakdown</div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                              <div style={styles.priceBox}>
                                <div style={styles.priceHeader}>Price Summary</div>

                                <div style={styles.priceRow}>
                                  <span style={styles.priceLabel}>Base Fare</span>
                                  <span style={styles.priceValue}>INR {Math.round(baseFare)?.toLocaleString("en-IN")}</span>
                                </div>

                                <div style={styles.priceRow}>
                                  <span style={styles.priceLabel}>Airline Tax</span>
                                  <span style={styles.priceValue}>INR {Math.round(customerAirlineTax)?.toLocaleString("en-IN")}</span>
                                </div>

                                {displayedCustomerConvenience > 0 && (
                                  <div style={styles.priceRow}>
                                    <span style={styles.priceLabel}>Convenience Fee (18% GST Included)</span>
                                    <span style={styles.priceValue}>INR {Math.round(displayedCustomerConvenience)?.toLocaleString("en-IN")}</span>
                                  </div>
                                )}

                                {discountVal > 0 && (
                                  <div style={styles.priceRow}>
                                    <span style={styles.priceLabel}>Discount</span>
                                    <span style={styles.discountValue}>- INR {discountVal?.toLocaleString("en-IN")}</span>
                                  </div>
                                )}

                                <div style={styles.priceRowTotal}>
                                  <span>Total Payable</span>
                                  <span>INR {Math.round(calculatedTotalCustomer)?.toLocaleString("en-IN")} INR</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div style={styles.infoSection}>
                            <div style={styles.infoTitleBar}>Important Information</div>
                            <ul style={styles.infoList}>
                              <li style={styles.infoListItem}>1 - You must web check-in on the airline website and obtain a boarding pass.</li>
                              <li style={styles.infoListItem}>2 - Reach the terminal at least 2 hours prior to the departure for domestic flight and 4 hours prior to the departure of international flight.</li>
                              <li style={styles.infoListItem}>3 - For departure terminal please check with the airline first.</li>
                              <li style={styles.infoListItem}>4 - Date &amp; Time is calculated based on the local time of the city/destination.</li>
                              <li style={styles.infoListItem}>5 - Use the Airline PNR for all Correspondence directly with the Airline</li>
                              <li style={styles.infoListItem}>6 - For rescheduling/cancellation within 4 hours of the departure time contact the airline directly</li>
                              <li style={styles.infoListItem}>7 - Your ability to travel is at the sole discretion of the airport authorities and we shall not be held responsible.</li>
                            </ul>

                            <div style={styles.dangerSection}>
                              {/* Left side: Forbidden Goods */}
                              <div>
                                <div style={styles.dangerHeader}>
                                  Dangerous goods — not permitted as hand or check-in baggage
                                </div>
                                <div style={styles.dangerGrid}>
                                  {["Lighters", "Flammable Liquids", "Toxic Substances", "Bleach", "Explosives", "Infectious Substances", "Pepper Spray", "Radioactive Materials", "Flammable Gas", "Corrosive Substances"].map((item) => (
                                    <div key={item} style={styles.dangerItem}>— {item}</div>
                                  ))}
                                </div>
                              </div>

                              {/* Right side: Allowed Goods */}
                              <div style={{ borderLeft: `1px solid ${line}`, paddingLeft: "32px" }}>
                                <div style={styles.dangerHeader}>
                                  Items allowed only in hand baggage
                                </div>
                                <div style={{ ...styles.dangerGrid, gridTemplateColumns: "1fr" }}>
                                  <div style={styles.dangerItem}>— Power Banks</div>
                                  <div style={styles.dangerItem}>— Lithium Batteries</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Footer */}
                          <div style={styles.invoiceFooter}>
                            <div style={styles.footerNote}>
                              This is a computer-generated invoice. No signature is required. For queries, contact us at {user?.email}.
                              All prices are inclusive of applicable taxes.
                            </div>
                            <div style={styles.footerBrand}>
                              <div style={styles.footerBrandName}>{user?.companyname}</div>
                              <div style={styles.footerBrandSub}>Authorized Travel Agency &bull; {moment().format("YYYY")}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerFlightInvoice;
